// ProductForm.js
import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Switch from 'components/_UI/Switch';
import ImageProduct from './parts/ImageProduct';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fieldValidations from 'helpers/fieldValidations';
import imageUploadApi from 'api/requests/imageUpload';
import productsApi from 'api/requests/products';
import { HorizontalCenter } from 'styled/alignment/Center';
import { useUser } from 'context/user/User';
import ticketsApi from 'api/requests/tickets';

export default function ProductForm({
  selectedProduct,
  activeTab,
  onClose = () => {},
  onSuccess = () => {},
  onError = () => {},
}) {
  const { user } = useUser()
  const [isLoading, setIsLoading] = useState(false);
  const [resetState, setResetState] = useState(false); 
  const [preUploadedImage, setPreUploadedImage] = useState(null);
  const [currentFormConfig, setCurrentFormConfig] = useState({})
  const [isImageMarkedForDeletion, setIsImageMarkedForDeletion] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      productName: '',
      description: '',
      quantity: '',
      price: '',
      is_active: false,
    }
  });

  const preUploadImage = (file) => {
    setPreUploadedImage(file)
    setIsImageMarkedForDeletion(false)
  }

  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', 'products');

    const { data } = await imageUploadApi.uploadWithFolder(formData);
    
    return data
  }

  const markImageForDeletion = () => {
    setIsImageMarkedForDeletion(true)
    setPreUploadedImage(null)
  }

  const onResetForm = () => {
    setPreUploadedImage(null)
    setIsImageMarkedForDeletion(false)
    reset({
      productName: '',
      description: '',
      quantity: '',
      price: '',
      is_active: false
    });
    setResetState(prev => !prev); 
  };

  const handleCurrencyChange = (value) => {
    if (value) {
        const formattedValue = value.replace('.', ''); 
        return formattedValue;
    }
    return '';
  };

  const createBaseProductPayload = (content) => {
    console.log(content)
    const productName = content.productName;
    const parsedPrice = parseFloat(content.price.replace(',', '.')).toFixed(2);
    const parsedQuantity = parseInt(content.quantity);

    const payload = {
      name: productName,
      price: parsedPrice,
      type: 'product',
      quantity: parsedQuantity,
      description: content.description,
      image_id: content?.image?.id,
      image: content?.image?.path
    };

    if (!payload.description) {
      delete payload.description;
    }

    if (!payload.image_id) {
      delete payload.image_id;
      delete payload.image;
    }

    if (content?.batches) {
      payload.batches = [
        {
          ...content?.batches[0],
          is_active: content.is_active,
          name: productName,
          price: parsedPrice,
          quantity: parsedQuantity,
        }
      ];

      delete payload.batches[0]?.quantity_available;
    }

    return payload;
  };

  const createProduct = async (content) => {
    try {
      setIsLoading(true);

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      const payload = createBaseProductPayload(content);

      await productsApi.create(payload);

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      onError();
    } finally {
      onClose();
    }
  };

  const editProduct = async (content) => {
    try {
      setIsLoading(true);

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      if (isImageMarkedForDeletion && selectedProduct?.image_id) {
        await imageUploadApi.delete(selectedProduct.image_id)
      }

      const payload = createBaseProductPayload(content);
      await productsApi.update(selectedProduct?.id, payload);

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      onError();
    } finally {
      onClose();
    }
  };

  const sellEstablishmentProduct = async (content) => {
    if (!user || !user.establishments || !user.establishments[0] || !user.establishments[0].id) {
      // handle no establishment error
      return
    }

    try {
      setIsLoading(true)

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      const payload = {
        ...createBaseProductPayload(content),
        establishment_id: user.establishments[0].id,
        sale_type: 'batch'
      }
  
      await ticketsApi.create(payload)

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      console.error(error)
      onError(error)
    } finally {
      onClose()
    }
  }

  const editEstablishmentProduct = async (content) => {
    try {
      setIsLoading(true)

      if (preUploadedImage) {
        content.image = await uploadImageToServer(preUploadedImage)
      }

      if (isImageMarkedForDeletion && selectedProduct?.image_id) {
        await imageUploadApi.delete(selectedProduct.image_id)
      }

      const payload = createBaseProductPayload(content)
  
      await ticketsApi.update(selectedProduct.id, payload)

      onResetForm();
      onSuccess(selectedProduct ? selectedProduct.action : 'create');
    } catch (error) {
      console.error(error)
      onError(error)
    } finally {
      onClose()
    }
  }

  const onSubmit = async (formData) => {
    if (selectedProduct) {
      const { action } = selectedProduct;
      
      if (action && action === 'sell_establishment') {
        await sellEstablishmentProduct(formData);

        return;
      }

      if (action && action === 'edit') {
        if (activeTab === 'establishment') {
          await editEstablishmentProduct({
            batches: selectedProduct?.batches,
            ...formData
          });
        }
        
        if (activeTab === 'template') {
          await editProduct(formData);
        }
      }
    } 

    if (!selectedProduct) {
      if (activeTab === 'establishment') {
        await sellEstablishmentProduct(formData);
      } 
      
      if (activeTab === 'template') {
        await createProduct(formData);
      }
    }
  };

  const formConfig = {
    template: {
      create: {
        formTitle: 'Criar produto',
        submitButtonLabel: 'Criar produto',
      },
      edit: {
        formTitle: 'Editar produto',
        submitButtonLabel: 'Editar produto',
      },
      sell_establishment: {
        formTitle: 'Vender produto no estabelecimento',
        submitButtonLabel: 'Vender produto',
      },
    },
    establishment: {
      create: {
        formTitle: 'Vender produto no estabelecimento',
        submitButtonLabel: 'Vender produto',
      },
      edit: {
        formTitle: 'Editar produto a venda',
        submitButtonLabel: 'Editar produto',
      },
    }
  }

  useEffect(() => {
    if (selectedProduct) {
      reset({
        productName: selectedProduct.name,
        description: selectedProduct.description || '',
        quantity: selectedProduct.quantity,
        price: selectedProduct.price,
        is_active: selectedProduct?.batches?.[0]?.is_active !== undefined ? Boolean(selectedProduct?.batches?.[0]?.is_active) : null,
      });
      setCurrentFormConfig(formConfig[activeTab][selectedProduct.action])
      setPreUploadedImage(null)
      setIsImageMarkedForDeletion(false)
    } 

    if (!selectedProduct) {
      onResetForm();
      setCurrentFormConfig(formConfig[activeTab].create)
    }
  }, [selectedProduct]);

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <Text name='title' marginBottom={'20px'} color={colors.occasionalPurple}>
        {currentFormConfig.formTitle}
      </Text>

      <ContentInputs>
        <Column verticalCenter>
          <Fieldset
            layout='squired'
            label='Nome do produto'
            name="productName"
            validations={fieldValidations.generic('nome', 2, 100)}
            register={register}
          />

          {errors.productName && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.productName.message}</Text>
            </HorizontalCenter>
          )}

          <Fieldset
            layout='squired'
            label='Descrição do produto'
            name='description'
            validations={fieldValidations.description('descrição', 200)}
            register={register}
          />
          {errors.description && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.description.message}</Text>
            </HorizontalCenter>
          )}

          <Fieldset
            label='Quantidade'
            layout='squired'
            name='quantity'
            validations={fieldValidations.number('quantidade')}
            register={register}
          />

          {errors.quantity && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.quantity.message}</Text>
            </HorizontalCenter>
          )}

          <Controller
            name='price'
            control={control}
            rules={{ required: 'O preço é obrigatório' }}
            render={({ field: { onChange, value, ref } }) => (
                <CurrencyInputField
                    label='Preço sugerido'
                    value={value}
                    layout='squired'
                    onChange={(value) => {
                        const formattedValue = handleCurrencyChange(value);
                        onChange(formattedValue); // Passa o valor formatado para onChange
                    }}
                    ref={ref}
                />
            )}
          />

          {errors.price && (
            <HorizontalCenter marginTop={'10px'}>
              <Text color={colors.danger}>{errors.price.message}</Text>
            </HorizontalCenter>
          )}

          {
            getValues('is_active') !== null &&
              <Column>
                <Text
                  name='small'
                  type='bold'
                  color={colors.nightDark}
                  paddingBottom={'5px'}
                >
                  Produto ativo
                </Text>
                <Controller
                  name='is_active'
                  control={control}
                  render={({ field: { onChange, value, ref } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Column>
          }
        </Column>

        <Column verticalCenter>
          <ImageProduct
            product
            selectedProduct={selectedProduct}
            onToggleImage={preUploadImage}
            onDeleteFile={markImageForDeletion}
            resetState={resetState}
          />
        </Column>
      </ContentInputs>

      {!isLoading ? (
        <ContentButton>
          <Button marginTop={'20px'} type="submit" variant="featured">
            {currentFormConfig.submitButtonLabel}
          </Button>
        </ContentButton>
      ) : (
        <Loader />
      )}
    </StyledForm>
  );
}

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentInputs = styled(Column)`
  align-items: center;
  @media screen and (min-width: ${breakpoints.sm}) {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

const ContentButton = styled.div`
  width: 100%;
`;
